import React, { useState } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import CustomLoader from '../components/Loader/CustomLoader'
import DataTable from 'react-data-table-component'
import { updateWithdrawReqStatus, withdrawRequestQuery } from '../apollo'
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Select,
  MenuItem
} from '@mui/material'
import orderBy from 'lodash/orderBy'
import SearchIcon from '@mui/icons-material/Search'
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PendingIcon from '@mui/icons-material/Pending'

const WITHDRAW_REQUEST_QUERY = gql`${withdrawRequestQuery}`
const UPDATE_WITHDRAW_STATUS = gql`${updateWithdrawReqStatus}`

const statusConfig = {
  REQUESTED: { bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
  TRANSFERRED: { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
  CANCELLED: { bg: '#fef2f2', color: '#991b1b', border: '#fecaca' }
}

const WithdrawRequest = () => {
  const [searchQuery, setSearchQuery] = useState('')

  const { loading, error, data } = useQuery(WITHDRAW_REQUEST_QUERY)
  const [updateStatus] = useMutation(UPDATE_WITHDRAW_STATUS)

  const allRequests = data?.getAllWithdrawRequests ?? []

  const regex = searchQuery.length > 2 ? new RegExp(searchQuery.toLowerCase(), 'g') : null
  const filtered = searchQuery.length < 3 ? allRequests : allRequests.filter(r =>
    r._id?.toLowerCase().search(regex) > -1 ||
    r.rider?.name?.toLowerCase().search(regex) > -1 ||
    r.status?.toLowerCase().search(regex) > -1
  )

  const requestedCount = allRequests.filter(r => r.status === 'REQUESTED').length
  const transferredCount = allRequests.filter(r => r.status === 'TRANSFERRED').length
  const totalAmount = allRequests.filter(r => r.status === 'REQUESTED').reduce((sum, r) => sum + (r.amount || 0), 0)

  const StatusSelect = ({ row }) => (
    <Select size="small" value={row.status} displayEmpty
      sx={{ fontSize: '0.72rem', borderRadius: '8px', minWidth: 130, bgcolor: '#f8fafc', '& .MuiSelect-select': { py: '4px' } }}
      onChange={() => {}}>
      {['REQUESTED', 'TRANSFERRED', 'CANCELLED'].map(s => (
        <MenuItem key={s} value={s} sx={{ fontSize: '0.75rem' }}
          onClick={() => {
            if (row.status !== s) {
              updateStatus({ variables: { id: row._id, status: s } })
            }
          }}>
          {s.charAt(0) + s.slice(1).toLowerCase()}
        </MenuItem>
      ))}
    </Select>
  )

  const columns = [
    {
      name: 'REQUEST ID', hide: 'md', minWidth: '140px',
      cell: row => <Typography sx={{ fontWeight: 600, fontSize: '0.78rem', color: '#1e293b', fontFamily: 'monospace' }}>#{row._id?.slice(-8).toUpperCase()}</Typography>
    },
    {
      name: 'RIDER', sortable: true, selector: row => row.rider?.name, minWidth: '180px',
      cell: row => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <Box sx={{ width: 34, height: 34, borderRadius: '9px', bgcolor: '#eff6ff', border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <TwoWheelerIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#1e293b' }}>{row.rider?.name ?? '—'}</Typography>
            {row.rider?.currentWalletAmount != null && (
              <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>Wallet: ${row.rider.currentWalletAmount.toFixed(2)}</Typography>
            )}
          </Box>
        </Box>
      )
    },
    {
      name: 'AMOUNT', sortable: true, selector: row => row.amount, minWidth: '120px',
      cell: row => <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a' }}>${(row.amount || 0).toFixed(2)}</Typography>
    },
    {
      name: 'DATE', hide: 'sm', sortable: true, selector: row => row.createdAt, minWidth: '140px',
      cell: row => <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>{new Date(row.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Typography>
    },
    {
      name: 'STATUS', minWidth: '220px',
      cell: row => {
        const sc = statusConfig[row.status] ?? { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Chip label={row.status} size="small" sx={{ bgcolor: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, fontWeight: 700, fontSize: '0.65rem', height: 22 }} />
            <StatusSelect row={row} />
          </Box>
        )
      }
    }
  ]

  return (
    <>
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1e40af 100%)', px: { xs: 2, md: 4 }, pt: 3.5, pb: 5, position: 'relative', overflow: 'hidden', '&::before': { content: '""', position: 'absolute', top: -60, right: -60, width: 260, height: 260, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' } }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: '14px', background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <AccountBalanceWalletIcon sx={{ color: '#93c5fd', fontSize: 26 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: '#fff', lineHeight: 1.15, letterSpacing: '-0.02em' }}>Rider Payouts</Typography>
            <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', mt: 0.3 }}>Manage rider wallet withdrawal requests</Typography>
          </Box>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 3, pb: 4 }}>
        <Box display="flex" gap={2} mb={3} flexWrap="wrap">
          {[
            { label: 'Pending Requests', value: requestedCount, icon: <PendingIcon sx={{ fontSize: 22 }} />, bg: 'linear-gradient(135deg,#fef3c7,#fde68a)', border: '#fde68a', iconBg: '#fef9c310', iconBorder: '#fde68a30', color: '#92400e' },
            { label: 'Transferred', value: transferredCount, icon: <CheckCircleIcon sx={{ fontSize: 22 }} />, bg: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', border: '#bbf7d0', iconBg: '#22c55e18', iconBorder: '#22c55e30', color: '#166534' },
            { label: 'Pending Amount', value: `$${totalAmount.toFixed(2)}`, icon: <AccountBalanceWalletIcon sx={{ fontSize: 22 }} />, bg: 'linear-gradient(135deg,#eff6ff,#dbeafe)', border: '#bfdbfe', iconBg: '#3b82f618', iconBorder: '#3b82f630', color: '#1e40af' }
          ].map(stat => (
            <Paper key={stat.label} elevation={0} sx={{ background: stat.bg, border: `1px solid ${stat.border}`, borderRadius: '16px', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', minWidth: 180 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: '12px', background: stat.iconBg, border: `1px solid ${stat.iconBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: stat.color, flexShrink: 0 }}>{stat.icon}</Box>
              <Box>
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>{stat.value}</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 500, mt: 0.2 }}>{stat.label}</Typography>
              </Box>
            </Paper>
          ))}
        </Box>

        <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
          <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', background: '#fafbfc' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>
              All Requests <Typography component="span" sx={{ ml: 1, fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>({filtered.length})</Typography>
            </Typography>
            <TextField size="small" placeholder="Search requests..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>, sx: { borderRadius: '10px', fontSize: '0.83rem', bgcolor: '#fff' } }}
              sx={{ width: 260 }} />
          </Box>
          {error && <Box sx={{ px: 3, py: 2, bgcolor: '#fef2f2' }}><Typography sx={{ fontSize: '0.83rem', color: '#dc2626' }}>Error: {error.message}</Typography></Box>}
          {loading ? <CustomLoader /> : (
            <DataTable columns={columns} data={filtered} pagination noHeader
              sortFunction={(rows, field, dir) => orderBy(rows, row => row[field]?.toString().toLowerCase(), dir)}
              customStyles={{ headRow: { style: { backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', minHeight: '44px' } }, headCells: { style: { fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase' } }, rows: { style: { borderBottom: '1px solid #f1f5f9', minHeight: '64px', '&:hover': { backgroundColor: '#f8fafc' } } }, cells: { style: { paddingLeft: '16px', paddingRight: '16px' } } }}
              noDataComponent={<Box py={8} textAlign="center"><AccountBalanceWalletIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} /><Typography sx={{ color: '#64748b' }}>No payout requests</Typography></Box>}
            />
          )}
        </Paper>
      </Container>
    </>
  )
}

export default withTranslation()(WithdrawRequest)